/**
 * Filter Chips Component
 * Quick filter chips for TAB-001
 * Now with breed multi-select dropdown and AI profile integration
 */

import { useState, useMemo } from 'react';
import { X, Check, Heart } from 'lucide-react';
import { toast } from 'sonner';
import iconFiltersBrown from '@/assets/icon-filters-brown.png';
import type { CatalogFiltersState, SortOption } from '@/hooks/useCatalogFilters';
import type { AIFilterProfile } from '@/hooks/useAIFilterProfile';
import { useAIMatchModal, AIMatchModal } from './AIMatchModal';
import { isAIProcessingEnabled } from '@/hooks/useUserSettings';
import { analyticsService } from '@/lib/services';
import { useSpeciesCounts } from '@/hooks/useSpeciesCounts';
import BreedDropdown from './BreedDropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SelectedBreed {
  id: string;
  name: string;
}

interface FilterChipsProps {
  filters: CatalogFiltersState;
  onSpeciesChange: (key: string | null) => void;
  onAgeBucketToggle: (bucket: string) => void;
  onSortChange: (sort: SortOption) => void;
  onOpenFiltersModal: () => void;
  activeCount: number;
  // Breed handlers
  onBreedToggle: (breedId: string, breedName: string) => void;
  onBreedRemove: (breedId: string) => void;
  selectedBreeds: SelectedBreed[];
  // Reset handler
  onResetFilters: () => void;
  // AI profile integration
  aiProfile?: AIFilterProfile | null;
  onApplyAIProfile?: (filters: Partial<CatalogFiltersState>) => void;
  // AI filter active state (managed by parent)
  isAIFilterActive?: boolean;
  onAIFilterActiveChange?: (active: boolean) => void;
}

interface ChipProps {
  label: string;
  badge?: string | number;
  isActive?: boolean;
  isFilter?: boolean;
  isAI?: boolean;
  flexGrow?: boolean;
  onClick: () => void;
  tooltip?: string;
}

const AI_TOOLTIP_TEXT = "Фильтр на основе ИИ подбирает питомцев, которые подходят именно вам, на основе вашей анкеты.";

const Chip = ({ label, badge, isActive, isFilter, isAI, flexGrow, onClick, tooltip }: ChipProps) => {
  // AI chip: uses secondary background when inactive, accent when active
  // Filter chip: always uses accent
  // Regular chip: accent when active, secondary when inactive
  const bgClass = isActive 
    ? 'bg-bg-accent-pink' 
    : isAI
      ? 'bg-bg-accent-pink-secondary'  // AI inactive = secondary (doesn't stand out)
      : isFilter
        ? 'bg-bg-accent-pink' 
        : 'bg-bg-accent-pink-secondary';
  
  const chipContent = (
    <button
      onClick={onClick}
      className={`relative flex items-center justify-center gap-2 h-[40px] px-4 ${bgClass} transition-colors active:opacity-80 ${flexGrow ? 'flex-1' : ''} ${isActive ? 'ring-2 ring-text-primary/30' : ''}`}
      style={{
        borderRadius: '20px',
        fontFamily: '"CoFo Gothic", system-ui, sans-serif',
      }}
    >
      {isFilter && (
        <img
          src={iconFiltersBrown}
          alt=""
          className="w-5 h-5 object-contain"
        />
      )}
      
      {isAI && (
        <Heart 
          className={`w-5 h-5 ${isActive ? 'fill-text-primary' : ''}`}
          style={{ color: '#3F2516' }}
        />
      )}
      
      {isActive && !isFilter && !isAI && (
        <Check className="w-4 h-4 text-text-primary" />
      )}
      
      <span className="text-text-primary text-[14px] font-medium whitespace-nowrap">
        {label}
      </span>

      {badge !== undefined && badge !== 0 && (
        <span
          className="absolute -top-2 right-2 min-w-[20px] px-1.5 py-0.5 bg-bg-accent-pink text-text-primary text-[10px] font-medium rounded-full text-center"
          style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
        >
          {badge}
        </span>
      )}
    </button>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {chipContent}
          </TooltipTrigger>
          <TooltipContent 
            className="max-w-[250px] text-center bg-white text-text-primary border-border"
            style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
          >
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return chipContent;
};

// Species labels
const SPECIES_LABELS: Record<string, string> = {
  'dog': 'Собаки',
  'cat': 'Кошки',
};

const FilterChips = ({
  filters,
  onSpeciesChange,
  onAgeBucketToggle,
  onSortChange,
  onOpenFiltersModal,
  activeCount,
  onBreedToggle,
  onBreedRemove,
  selectedBreeds,
  onResetFilters,
  aiProfile,
  onApplyAIProfile,
  isAIFilterActive = false,
  onAIFilterActiveChange,
}: FilterChipsProps) => {
  // Use cached species counts hook
  const speciesCounts = useSpeciesCounts({
    breedIds: filters.breedIds,
    sex: filters.sex,
    ageBuckets: filters.ageBuckets,
    geoPlaceId: filters.geoPlaceId,
    sizes: filters.sizes,
    colors: filters.colors,
    healthSpecialNeeds: filters.healthSpecialNeeds,
  });
  
  // AI Match modal hook
  const {
    showModal: isAIModalOpen,
    setShowModal: setIsAIModalOpen,
    openModal,
    shouldShowModal,
    surveyCompleted,
  } = useAIMatchModal();

  const handleAIChipClick = () => {
    // Check if AI processing is enabled in settings
    if (!isAIProcessingEnabled()) {
      toast.info('ИИ-обработка отключена в настройках', {
        description: 'Включите её в разделе Профиль → Настройки → ИИ-обработка',
      });
      return;
    }
    
    // If already active, toggle OFF - reset filters
    if (isAIFilterActive) {
      onAIFilterActiveChange?.(false);
      onResetFilters();
      return;
    }
    
    // If survey not completed OR modal was never shown, always open modal
    if (shouldShowModal()) {
      openModal();
    } else if (aiProfile && onApplyAIProfile) {
      // Apply AI profile filters
      onApplyAIProfile(aiProfile.filters);
      onAIFilterActiveChange?.(true);
      
      // Track analytics
      const f = aiProfile.filters;
      analyticsService.trackCatalogFilterApply('CAT-001', {
        species: f.speciesKey ?? null,
        breed_ids: f.breedIds ?? [],
        breed_count: (f.breedIds ?? []).length,
        sex: f.sex ?? [],
        age_buckets: f.ageBuckets ?? [],
        geo_place_id: f.geoPlaceId ?? null,
        has_geo: !!f.geoPlaceId,
        sizes: f.sizes ?? [],
        colors: f.colors ?? [],
        health_special_needs: f.healthSpecialNeeds ?? null,
        filter_source: 'ai_profile',
        active_filters_count: [
          f.speciesKey,
          (f.breedIds ?? []).length > 0,
          (f.sex ?? []).length > 0,
          (f.ageBuckets ?? []).length > 0,
          f.geoPlaceId,
          (f.sizes ?? []).length > 0,
        ].filter(Boolean).length,
      });
    } else {
      // No AI profile yet, open modal to complete quiz
      openModal();
    }
  };

  const handleBreedToggle = (breed: { id: string; name: string }) => {
    onBreedToggle(breed.id, breed.name);
  };

  return (
    <>
      <div
        data-testid="tab-001-filter-chips"
        className="flex flex-col gap-2"
      >
        {/* Row 1: AI filter + Species filters */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pt-2">
          <Chip 
            label="Для вас"
            isAI
            isActive={isAIFilterActive}
            onClick={handleAIChipClick}
            tooltip={AI_TOOLTIP_TEXT}
          />
          <Chip 
            label={SPECIES_LABELS['dog']} 
            badge={speciesCounts['dog'] || undefined}
            isActive={filters.speciesKey === 'dog'}
            onClick={() => {
              const newSpecies = filters.speciesKey === 'dog' ? null : 'dog';
              onSpeciesChange(newSpecies);
              analyticsService.trackCatalogFilterApply('CAT-001', {
                species: newSpecies,
                breed_ids: filters.breedIds,
                breed_count: filters.breedIds.length,
                sex: filters.sex,
                age_buckets: filters.ageBuckets,
                geo_place_id: filters.geoPlaceId,
                has_geo: !!filters.geoPlaceId,
                sizes: filters.sizes,
                colors: filters.colors,
                health_special_needs: filters.healthSpecialNeeds,
                filter_source: 'chip',
                active_filters_count: newSpecies ? 1 : 0,
              });
            }}
          />
          <Chip 
            label={SPECIES_LABELS['cat']} 
            badge={speciesCounts['cat'] || undefined}
            isActive={filters.speciesKey === 'cat'}
            onClick={() => {
              const newSpecies = filters.speciesKey === 'cat' ? null : 'cat';
              onSpeciesChange(newSpecies);
              analyticsService.trackCatalogFilterApply('CAT-001', {
                species: newSpecies,
                breed_ids: filters.breedIds,
                breed_count: filters.breedIds.length,
                sex: filters.sex,
                age_buckets: filters.ageBuckets,
                geo_place_id: filters.geoPlaceId,
                has_geo: !!filters.geoPlaceId,
                sizes: filters.sizes,
                colors: filters.colors,
                health_special_needs: filters.healthSpecialNeeds,
                filter_source: 'chip',
                active_filters_count: newSpecies ? 1 : 0,
              });
            }}
          />
        </div>

        {/* Row 2: Breed dropdown + selected breed chips + Filters button */}
        <div className="flex gap-2 flex-wrap items-center">
          {/* Breed dropdown */}
          <BreedDropdown
            speciesKey={filters.speciesKey}
            selectedBreedIds={filters.breedIds}
            onToggle={handleBreedToggle}
          />

          {/* Selected breed chips with X to remove */}
          {selectedBreeds.map((breed) => (
            <button
              key={breed.id}
              onClick={() => onBreedRemove(breed.id)}
              className="flex items-center gap-1.5 h-[40px] px-3 bg-bg-accent-pink rounded-full ring-2 ring-text-primary/30 transition-colors active:opacity-80"
              style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
            >
              <span className="text-text-primary text-[14px] font-medium">{breed.name}</span>
              <X className="w-4 h-4 text-text-primary" />
            </button>
          ))}

          {/* Filters button with inline reset */}
          <div className="flex items-center flex-1">
            <button
              onClick={onOpenFiltersModal}
              className={`relative flex items-center justify-center gap-2 h-[40px] px-4 bg-bg-accent-pink transition-colors active:opacity-80 flex-1 ${activeCount > 0 ? 'rounded-l-full' : 'rounded-full'}`}
              style={{
                borderRadius: activeCount > 0 ? '20px 0 0 20px' : '20px',
                fontFamily: '"CoFo Gothic", system-ui, sans-serif',
              }}
            >
              <img
                src={iconFiltersBrown}
                alt=""
                className="w-5 h-5 object-contain"
              />
              <span className="text-text-primary text-[14px] font-medium whitespace-nowrap">
                Фильтры
              </span>
              {activeCount > 0 && (
                <span
                  className="absolute -top-2 right-2 min-w-[20px] px-1.5 py-0.5 bg-bg-accent-pink text-text-primary text-[10px] font-medium rounded-full text-center"
                  style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
                >
                  {activeCount}
                </span>
              )}
            </button>
            {activeCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onResetFilters();
                }}
                className="flex items-center justify-center h-[40px] w-[40px] bg-bg-accent-pink rounded-r-full border-l border-text-primary/10 transition-colors active:opacity-80"
                style={{ borderRadius: '0 20px 20px 0' }}
              >
                <X className="w-4 h-4 text-text-primary" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AI Match Modal */}
      <AIMatchModal
        open={isAIModalOpen}
        onOpenChange={setIsAIModalOpen}
        onComplete={() => {
          // Apply AI profile filters after modal is dismissed
          if (aiProfile && onApplyAIProfile) {
            onApplyAIProfile(aiProfile.filters);
            onAIFilterActiveChange?.(true);
          }
        }}
      />
    </>
  );
};

export default FilterChips;